// frontend/src/modules/reports/pages/workforce/LeaveReportPage.tsx
//
// Phase 4 (Reports Frontend), Task 3. Leave report — requests table
// (approved|pending pills, leave type name/code, date range) plus a balances
// panel per employee/type (allocated/used/carried_over). Named
// LeaveReportPage (not LeavePage) to avoid colliding with the hr module's
// own LeavePage import name.

import * as React from 'react';
import { CalendarDays, CheckCircle2, Clock3 } from 'lucide-react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { StatCard } from '../../../../design-system/components/StatCard';
import { DataTable } from '../../../../design-system/components/DataTable';
import { useApi } from '../../../../design-system/useApi';
import { reportUrl, downloadReportCsv } from '../../api';
import type { LeaveReport, LeaveRequestRow } from '../../types';
import { VISUAL, statusPill, type StatusTone } from '../../../../design-system/visual';
import { formatCalendarDate } from '../../../../design-system/format';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../../components/ui/select';
import type { EmployeeWithUser } from '../../../hr/types';

const STATUS_TONES: Record<LeaveRequestRow['status'], StatusTone> = {
  approved: 'success',
  pending: 'warning',
};

const STATUS_LABELS: Record<LeaveRequestRow['status'], string> = {
  approved: 'Approved',
  pending: 'Pending',
};

function typeLabel(name: string | null, code: string | null): string {
  if (name && code) return `${name} (${code})`;
  return name ?? code ?? '—';
}

export function LeaveReportPage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);
  const [employeeId, setEmployeeId] = React.useState<string>('all');

  const empResult = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = empResult.data?.employees ?? [];

  const path = range
    ? reportUrl('leave', {
      from: range.from,
      to: range.to,
      employeeId: employeeId !== 'all' ? employeeId : undefined,
    })
    : null;
  const result = useApi<{ report: LeaveReport }>(path, [employeeId]);
  const forbidden = result.status === 403;

  const report = result.data?.report ?? null;
  const rows = report?.rows ?? [];
  const balances = report?.balances ?? [];

  const counts = React.useMemo(() => {
    const approved = rows.filter((r) => r.status === 'approved').length;
    const pending = rows.filter((r) => r.status === 'pending').length;
    return { approved, pending };
  }, [rows]);

  return (
    <ReportShell
      eyebrow="Reports / Workforce"
      title="Leave"
      description="Leave requests and per-employee balances."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0 && balances.length === 0}
      filters={
        employees.length > 0 && (
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="h-9 rounded-full bg-card/80 border-border/60 px-3.5 text-[12.5px] font-semibold w-auto min-w-[180px]">
              <SelectValue placeholder="All employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )
      }
      csv={range ? {
        filename: `leave-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'leave',
          { from: range.from, to: range.to, employeeId: employeeId !== 'all' ? employeeId : undefined },
          `leave-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard label="Approved" value={counts.approved} icon={<CheckCircle2 />} />
            <StatCard label="Pending" value={counts.pending} icon={<Clock3 />} />
          </div>

          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable
              rows={rows}
              rowKey={(r) => r.id}
              emptyState={<div className="p-6 text-sm text-muted-foreground">No leave requests for this period.</div>}
              columns={[
                { key: 'employee', header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
                { key: 'type', header: 'Type', cell: (r) => typeLabel(r.leave_type_name, r.leave_type_code) },
                { key: 'dates', header: 'Dates', cell: (r) => `${formatCalendarDate(r.start_date)} – ${formatCalendarDate(r.end_date)}` },
                { key: 'status', header: 'Status', cell: (r) => <span className={statusPill(STATUS_TONES[r.status])}>{STATUS_LABELS[r.status]}</span> },
                { key: 'reason', header: 'Reason', cell: (r) => r.reason ?? '—' },
              ]}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 px-1">
              <CalendarDays className="h-4 w-4 text-muted-foreground/70" />
              <h3 className="text-[13px] font-semibold text-foreground">Balances</h3>
            </div>
            <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
              <DataTable
                rows={balances}
                rowKey={(r) => `${r.employee_id}-${r.leave_type_id}-${r.year}`}
                emptyState={<div className="p-6 text-sm text-muted-foreground">No leave balances configured.</div>}
                columns={[
                  { key: 'employee', header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
                  { key: 'type', header: 'Type', cell: (r) => typeLabel(r.leave_type_name, r.leave_type_code) },
                  { key: 'year', header: 'Year', cell: (r) => r.year },
                  { key: 'allocated', header: 'Allocated', cell: (r) => r.allocated },
                  { key: 'used', header: 'Used', cell: (r) => r.used },
                  { key: 'carried_over', header: 'Carried over', cell: (r) => r.carried_over },
                ]}
              />
            </div>
          </div>
        </>
      )}
    </ReportShell>
  );
}
